import { Container, Point, Sprite } from 'pixi.js'
import LayerBase from './LayerBase'
import Item from '@/scripts/canvas/Item'
import { getActivityByItemId } from '@/scripts/data/activityQueries'
import type { ILayer, IItemInfo, IItem } from '@/scripts/data/types'
import { dimensions } from '@/scripts/config/settings'

export default class Layer extends LayerBase {
  public readonly items: Item[] = []
  private readonly extraContainer: Container

  constructor(layerSize: Point, layerData: ILayer) {
    super(layerData.name, layerSize, dimensions)
    this.sortableChildren = true

    this.createBackground(layerData.background)
    this.createItems(layerData.items)
    this.createForeground(layerData.foreground)
    this.extraContainer = new Container()
    this.extraContainer.eventMode = 'passive'
    this.extraContainer.zIndex = 100 // Ensure it's above all items
    this.addChild(this.extraContainer)
  }

  // Adds background sprites (lowest layer visuals).
  private createBackground(backgroundData?: IItemInfo[]): void {
    backgroundData?.forEach((bg) => {
      const sprite = Sprite.from(bg.id)
      sprite.position.set(bg.x, bg.y)
      sprite.zIndex = 0
      sprite.eventMode = 'passive'
      this.addChild(sprite)
    })
  }

  // Adds foreground sprites
  private createForeground(foreground?: IItemInfo[]): void {
    foreground?.forEach((fg) => {
      const sprite = Sprite.from(fg.id)
      sprite.position.set(fg.x, fg.y)
      sprite.zIndex = 5
      sprite.eventMode = 'passive'
      this.addChild(sprite)
    })
  }

  // Adds interactive items to the layer.
  private createItems(itemsData?: IItem[]): void {
    itemsData?.forEach((itemData) => {
      const activity = getActivityByItemId(itemData.id)
      if (!activity) return

      const item = new Item(itemData, activity)
      item.show(true, true)

      this.items.push(item)
      this.addChild(item)
    })
  }

  // Provides a container for special effects (e.g., snow, smoke).
  public addExtra(displayObject: Container, isInteractive: boolean = false): void {
    if (!isInteractive) {
      displayObject.eventMode = 'passive'
      displayObject.interactiveChildren = false
    }
    this.extraContainer.addChild(displayObject)
  }
}
